import React, {
  useState,
  Fragment,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { translate } from '../Services/translation';
import { useDispatch, useSelector } from 'react-redux';
import { handlePelletCart, loadError } from '../Actions/Cart.action';
import { debounce } from 'lodash-es';
import { catchError } from '../Actions/Error.action';
import { format } from 'react-string-format';

const mapCartToAbort = {};
const pelletCartDispatch = debounce(
  (areaCode, productType, quantity, dispatch) => {
    mapCartToAbort[areaCode] = new AbortController();
    dispatch(
      handlePelletCart(
        areaCode,
        productType,
        quantity || 0,
        mapCartToAbort[areaCode]
      )
    ).catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
  },
  500
);

function formatString(template, ...args) {
  return template
    .replace(',', '.')
    .replace(/{(\d+)}/g, (match, number) =>
      typeof args[number] !== 'undefined' ? args[number] : match
    );
}

function parseDecimal(num) {
  return num?.toString().replace(',', '.');
}

const PelletCart = () => {
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const [shippingClass, setShippingClass] = useState(cart?.orderRows);
  const [areaCode, setAreaCode] = useState(cart?.areaCode);
  const [quantity, setQuantity] = useState(cart?.minimumQuantity);
  const [inputQuantity, setInputQuantity] = useState();
  const [productType, setProductType] = useState(cart?.productType);
  const [invalidFreightZone, setInvalidFreightZone] = useState(false);
  const [logo, setLogo] = useState(null);
  const areaCodeRef = useRef(null);
  const quantityRef = useRef(null);

  useEffect(() => {
    setAreaCode(cart?.areaCode);
    setShippingClass(cart?.shippingClass);
    setProductType(cart?.productType);
    setInvalidFreightZone(cart?.shippingClass === 'NA');
    setQuantity(
      Math.max(
        parseDecimal(cart?.quantity),
        parseDecimal(cart?.minimumQuantity)
      )
    );
    setInputQuantity(parseDecimal(cart?.quantity));

    if (!areaCode?.length) {
      setAreaCode(areaCodeRef?.current?.value);
    }
  }, [cart]);

  useEffect(() => {
    if (areaCode !== cart?.areaCode) {
      handlePelletCart(areaCode, productType, quantity);
    }
  }, [areaCode]);

  useEffect(() => {
    const payload = window.__litium?.preloadState?.checkout?.checkoutLogo;

    if (payload) {
      setLogo({
        src: payload.getUrlToImage,
        alt: payload.alt,
        title: payload.title,
      });
    }
  }, [window?.__litium?.preloadState?.checkout]);

  useEffect(() => {
    handlePelletCart(areaCode, productType, parseDecimal(quantity));
  }, [productType]);

  useEffect(() => {
    if (
      Number(parseDecimal(quantity)) !== Number(parseDecimal(cart?.quantity)) &&
      Number(parseDecimal(quantity)) >=
        Number(parseDecimal(cart.minimumQuantity)) &&
      (Number(parseDecimal(quantity)) <=
        Number(parseDecimal(cart.maximumQuantity)) ||
        Number(parseDecimal(cart.maximumQuantity)) === 0)
    ) {
      handlePelletCart(areaCode, productType, parseDecimal(quantity));
    }
  }, [quantity]);

  const handlePelletCart = useCallback(
    (areaCode, productType = 'Pallet', quantity = 4) => {
      // In case of onBlur event, we need ensureCorrectness as true in order to ensure if the value is a valid float number
      // In case of onChange event, we accept invalid float number, but we don't send any request to the server.
      let validValue = !isNaN(areaCode) && areaCode?.length === 5;

      mapCartToAbort[areaCode] && mapCartToAbort[areaCode].abort();
      pelletCartDispatch && pelletCartDispatch.cancel();
      if (validValue) {
        pelletCartDispatch(
          areaCode,
          productType,
          parseDecimal(quantity),
          dispatch
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    setInputQuantity(parseDecimal(cart?.minimumQuantity));
    setQuantity(parseDecimal(cart?.minimumQuantity));
  }, [cart?.minimumQuantity]);

  const checkoutCartInfoText = translate(
    'checkout.cart.checkout.info.text'
  )?.split('\\n');
  const AreaCode = useCallback(() => {
    return (
      <Fragment>
        <label htmlFor="cart-freight-zone">
          <span>{translate('checkout.cart.freightzoneheader')}</span>
        </label>
        <input
          ref={areaCodeRef}
          className="checkout-cart__input"
          type="number"
          defaultValue={areaCode}
          onChange={(event) => setAreaCode(event.target.value)}
          onBlur={(event) => setAreaCode(event.target.value)}
          id="cart-freight-zone"
        />
        {shippingClass && !invalidFreightZone && (
          <div>
            <div className="checkout__price-info--reg-price">
              {translate('checkout.cart.basedonfreightzone')}
            </div>
            <div className="checkout__price-info--price">
              {translate('checkout.cart.yourfreightzone')} {cart.shippingClass}
            </div>
          </div>
        )}
        {shippingClass && invalidFreightZone && (
          <div className="checkout__price-info--price">
            {translate('checkout.cart.invalidfreightzone')}
          </div>
        )}
      </Fragment>
    );
  }, [handlePelletCart, shippingClass]);

  const CartTotal = useCallback(
    ({ cart, productType, shippingClass, invalidFreightZone }) => {
      const priceText = {
        Unpacked: translate('checkout.cart.yourprice.bulk'),
        Pallet: translate('checkout.cart.yourprice.pallet'),
      };

      return (
        <div>
          {shippingClass && !invalidFreightZone && (
            <>
              {cart.orderRows?.[0]?.totalPriceIncludingVat > 0 && (
                <div className="text--right checkout__price-info--reg-price">
                  {priceText[productType]}:{' '}
                  {productType === 'Unpacked'
                    ? cart.orderRows?.[0]?.calculatedPrice
                    : cart.orderRows?.[0]?.price}
                </div>
              )}
              {productType === 'Unpacked' && (
                <div className="text--right checkout__price-info--price">
                  {format(
                    translate('checkout.cart.reservation'),
                    cart.reservationAmount / 10
                  )}
                  : {cart.paymentCost}
                </div>
              )}
              {parseInt(cart.grandTotal) > 0 && (
                <h4 className="text--right checkout__price-info--total">
                  <span style={{ fontWeight: 300 }}>
                    {translate('checkout.cart.totalprice')}:
                  </span>{' '}
                  {cart.grandTotal}
                </h4>
              )}
            </>
          )}
        </div>
      );
    },
    []
  );

  useEffect(() => {
    const handleDebouncedQuantityUpdate = debounce((newQuantity) => {
      const validQuantity = Math.max(
        parseDecimal(newQuantity),
        parseDecimal(cart.minimumQuantity)
      );
      setQuantity(validQuantity);
    }, 500);

    handleDebouncedQuantityUpdate(parseDecimal(inputQuantity));
    return () => {
      handleDebouncedQuantityUpdate.cancel();
    };
  }, [inputQuantity]);

  const Quantity = useCallback(
    ({ inputQuantity, productType, minimumQuantity, touched, setTouched }) => {
      const headerText = {
        Unpacked: translate('checkout.cart.quantityunpacked'),
        Pallet: translate('checkout.cart.quantitypallet'),
        '': translate('checkout.cart.quantity'),
      };

      const headerTextString =
        inputQuantity &&
        Number(cart?.maximumQuantity) > 0 &&
        Number(inputQuantity) > Number(cart?.maximumQuantity)
          ? productType !== 'Unpacked'
            ? formatString(
                translate('checkout.cart.maximumquantity'),
                cart.maximumQuantity
              )
            : formatString(
                translate('checkout.cart.maximumquantityunpacked'),
                cart.maximumQuantity
              )
          : headerText[productType];

      return (
        <>
          <label htmlFor="cart-quantity">
            <span>
              {areaCode === ''
                ? translate('checkout.cart.quantitymissingareacode')
                : headerTextString}

              {productType &&
                minimumQuantity > 0 &&
                inputQuantity < minimumQuantity && (
                  <>
                    {' '}
                    ({translate('checkout.cart.minimumquantity')}{' '}
                    {minimumQuantity})
                  </>
                )}
            </span>
          </label>
          <input
            ref={quantityRef}
            className="checkout-cart__input"
            id="cart-quantity"
            step={productType === 'Unpacked' ? '0.1' : '1'}
            type="number"
            value={parseDecimal(inputQuantity)}
            onChange={(e) => setInputQuantity(parseDecimal(e.target.value))}
            onBlur={(e) => e.preventDefault()}
            disabled={areaCode === ''}
          />
        </>
      );
    },
    [cart.maximumQuantity]
  );

  return (
    <div className="row">
      <div className="banner-block__columns columns small-12 medium-12 large-4">
        <AreaCode />
      </div>
      <div className="banner-block__columns columns small-12 medium-12 large-4">
        <div className="pellets-product__type">
          <label
            className={areaCode === '' ? 'disabled' : ''}
            tabIndex={areaCode === '' ? '-1' : '0'}
            onKeyDown={(e) => {
              if (
                (e.key === 'Enter' || e.key === ' ') &&
                cart?.minimumQuantity >= 1
              ) {
                e.preventDefault();
                e.currentTarget.querySelector('input')?.click();
              }
            }}
          >
            <input
              type="radio"
              name="productType"
              value="Pallet"
              onChange={(e) => setProductType(e.target.value)}
              checked={productType === 'Pallet'}
              disabled={areaCode === ''}
            />
            <span className="front-end box">
              <span className={productType === 'Pallet' ? 'selected' : ''}>
                <span className="image"></span>
                {translate('checkout.cart.pallet')}
              </span>
            </span>
          </label>

          <label
            className={areaCode === '' ? 'disabled' : ''}
            tabIndex={areaCode === '' ? '-1' : '0'}
            onKeyDown={(e) => {
              if (
                (e.key === 'Enter' || e.key === ' ') &&
                cart?.minimumQuantity >= 1
              ) {
                e.preventDefault();
                e.currentTarget.querySelector('input')?.click();
              }
            }}
          >
            <input
              type="radio"
              name="productType"
              value="Unpacked"
              onChange={(e) => setProductType(e.target.value)}
              checked={productType === 'Unpacked'}
              disabled={areaCode === ''}
            />
            <span className="back-end box">
              <span className={productType === 'Unpacked' ? 'selected' : ''}>
                <span className="image"></span>
                {translate('checkout.cart.unpacked')}
              </span>
            </span>
          </label>
        </div>
      </div>
      <div className="banner-block__columns columns small-12 medium-12 large-4">
        <Quantity
          inputQuantity={inputQuantity}
          productType={productType}
          minimumQuantity={cart.minimumQuantity}
        />

        <CartTotal
          cart={cart}
          productType={productType}
          shippingClass={shippingClass}
          invalidFreightZone={invalidFreightZone}
        />
      </div>
      <div className="checkout-cart__bottom-container">
        {checkoutCartInfoText?.length && (
          <div className="checkout-cart__bottom-container-info">
            {checkoutCartInfoText?.map((text) => (
              <span key={text}>{text}</span>
            ))}
          </div>
        )}
        {shippingClass && !invalidFreightZone && parseInt(cart.grandTotal) > 0 && (
          <a
            href={cart.checkoutUrl}
            className={`button-cta form__button ${
              (inputQuantity > Number(cart.maximumQuantity) &&
                Number(cart.maximumQuantity) > 0) ||
              (inputQuantity < Number(cart.minimumQuantity) &&
                Number(cart.maximumQuantity) === 0) ||
              inputQuantity <= 0 ||
              inputQuantity === ''
                ? 'disabled'
                : ''
            }`}
          >
            {translate('checkout.cart.tocheckout')}
          </a>
        )}
        {logo && (
          <div className="checkout-cart__image">
            <img {...logo} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PelletCart;
